
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { formSchema, ContactFormValues } from "./schema";
import { NameFields, ContactFields, MessageField } from "./FormFields";
import { PrivacyConsent } from "./PrivacyConsent";
import { SubmitButton } from "./SubmitButton";

interface ContactFormProps {
  className?: string;
  submitLabel?: string;
  redirectUrl?: string;
  onSubmit?: (data: ContactFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
}

export function ContactForm({
  className,
  submitLabel = "Send message",
  redirectUrl,
  onSubmit,
  isSubmitting = false,
}: ContactFormProps) {
  const { toast } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
      consent: false,
    },
  });

  async function handleSubmit(data: ContactFormValues) {
    try {
      if (onSubmit) {
        const result = await onSubmit(data);
        
        if (result) {
          form.reset();
        }
      } else {
        console.log(data);
        
        // Default implementation when no onSubmit provided
        toast({
          title: "Enquiry sent",
          description: "Thanks for reaching out. We'll get back to you soon.",
        });
        
        form.reset();
        
        // Redirect if specified
        if (redirectUrl) {
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className={className}>
        <NameFields control={form.control} />
        <ContactFields control={form.control} />
        <MessageField control={form.control} />
        <PrivacyConsent control={form.control} />
        <SubmitButton isSubmitting={isSubmitting} submitLabel={submitLabel} />
      </form>
    </Form>
  );
}

// Fix the type re-export by using 'export type'
export type { ContactFormValues } from './schema';
